import React, { useEffect, useState } from "react";
import { fetchData, getValueLabel, fetchFormData } from "../../utils/options";
import PermiTable from "../shared/table/PermiTable";
import SelectDropdown from "../shared/SelectDropdown";
import topTost from "../../utils/topTost";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";

const PermissionTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const M_id = queryParams.get("id");
  const userId = localStorage.getItem("id");
  const [permission, setPermission] = useState(null);

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (localStorage.getItem("access_token") === null) {
      navigate("/");
    }
  }, []);

  useEffect(() => {
    const loadRoles = async () => {
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("moduleId", M_id);
      const res = await fetchFormData("role/listDrop", formData, "POST");
      if (res?.data) {
        setRoles(getValueLabel(res.data, "role", "id") || []);
      }
    };
    loadRoles();
  }, []);

  useEffect(() => {
    if (selectedRole?.value) {
      fetchRolePermissions(selectedRole.value);
    }
  }, [selectedRole]);

  const fetchRolePermissions = async (roleId) => {
    const res = await fetchData(`permissions/by-role/${roleId}`, {}, "POST");
    const modulesRes = await fetchData("moduleslist", {}, "POST");

    if (modulesRes?.modules) {
      const modules = modulesRes.modules;

      const defaultPermissions = modules.flatMap((mod) =>
        mod.submodules.map((sub) => {
          const existing = res?.permissions?.find(
            (p) => p.module_id === sub.ModuleId
          );

          return { 
            module_id: sub.ModuleId,
            module_name: mod.module_name,
            sub_module_name: sub.sub_module_name || "",
            pf_add: existing?.pf_add === 1 ? 1 : 0,
            pf_view: existing?.pf_view || 0,
            pf_edit: existing?.pf_edit || 0,
            pf_delete: existing?.pf_delete || 0,
            pf_status: existing?.pf_status || 0,
            pf_print: existing?.pf_print || 0,
          };
        })
      );

      setPermissions(defaultPermissions); // now this is a flat array
    }
  };

  const handlePermissionChange = (index, moduleId, selectedPerm) => {
    setPermissions((prevPermissions) => {
      const updatedPermissions = [...prevPermissions];
      const module = updatedPermissions[index];

      if (module && module.module_id === moduleId) {
        updatedPermissions[index] = {
          ...module,
          [selectedPerm]: module[selectedPerm] === 1 ? 0 : 1,
        };
      }

      return updatedPermissions;
    });
  };

  const savePermissions = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to save these permissions?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, save it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3454d1",
    });

    if (result.isConfirmed) {
      const res = await fetchData(
        "permissions/save",
        {
          role_id: selectedRole.value,
          permissions: permissions.map(
            ({
              module_id,
              pf_add,
              pf_view,
              pf_edit,
              pf_delete,
              pf_status,
              pf_print,
            }) => ({
              module_id: module_id,
              pf_add,
              pf_view,
              pf_edit,
              pf_delete,
              pf_status,
              pf_print,
            })
          ),
        },
        "POST"
      );

      if (res?.code === 200) {
        topTost({
          title: res?.code.message || "Permissions updated successfully",
          icon: "success",
        });
      } else {
        topTost({
          title: "Failed to update permissions.",
          icon: "error",
        });
      }
    }
  };
 
const getPermissionColumns = () => {
  const permissionFields = [
    "pf_add",
    "pf_view",
    "pf_edit",
    "pf_delete",
    "pf_status",
    "pf_print",
  ];

  const handleSelectAll = (perm, checked) => {
    const updatedPermissions = permissions.map((row) => ({
      ...row,
      [perm]: checked ? 1 : 0,
    }));
    setPermissions(updatedPermissions);
  };

  const handleRowSelectAll = (rowIndex, checked) => {
    const updatedPermissions = permissions.map((row, index) => {
      if (index === rowIndex) {
        const updatedRow = { ...row };
        permissionFields.forEach((perm) => {
          updatedRow[perm] = checked ? 1 : 0;
        });
        return updatedRow;
      }
      return row;
    });
    setPermissions(updatedPermissions);
  };

  return [
       {
      id: "row_select_all",
      header: "All",
      cell: ({ row }) => {
        const allChecked = permissionFields.every((perm) => row.original[perm] === 1);
        const someChecked = permissionFields.some((perm) => row.original[perm] === 1);

        return (
          <input
            type="checkbox"
            checked={allChecked}
            ref={(el) => {
              if (el) el.indeterminate = !allChecked && someChecked;
            }}
            onChange={(e) => handleRowSelectAll(row.index, e.target.checked)}
          />
        );
      },
      enableHiding: false,
      defaultIsVisible: true,
      enableSorting: false,
    },
    {
      accessorKey: "module_name",
      header: "Module / Sub Module",
      cell: ({ row }) => {
        const moduleName = row.original.module_name || "";
        const subModuleName = row.original.sub_module_name || "";
        return (
          <div>
            <strong className="fw-normal">{moduleName}</strong>
            {subModuleName && subModuleName !== moduleName ? (
              <div style={{ fontSize: "13px", color: "#555", fontWeight: "bold" }}>
                → {subModuleName}
              </div>
            ) : null}
          </div>
        );
      },
      enableHiding: false,
      defaultIsVisible: true,
    },
    
    ...permissionFields.map((perm) => {
      const allChecked = permissions.every((row) => row[perm] === 1);
      const someChecked = permissions.some((row) => row[perm] === 1);

      return {
        accessorKey: perm,
        header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <label style={{ fontSize: "12px" }}>{perm.toUpperCase().replace("PF_", "")}</label>
            <input
              type="checkbox"
              checked={allChecked}
              ref={(el) => {
                if (el) el.indeterminate = !allChecked && someChecked;
              }}
              onChange={(e) => handleSelectAll(perm, e.target.checked)}
            />
          </div>
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.original[perm] === 1}
            onChange={() =>
              handlePermissionChange(row.index, row.original.module_id, perm)
            }
          />
        ),
        enableHiding: false,
        defaultIsVisible: true,
        enableSorting: false,
      };
    }),
 
  ];
};

  return (
    <div>
      <div className="row mb-4 align-items-center">
        <div className="col-lg-2">
          <label className="fw-semibold">
            Select Role: <span style={{ color: "red" }}> *</span>
          </label>
        </div>
        <div className="col-lg-4">
          <SelectDropdown
            required={true}
            options={roles}
            value={selectedRole?.value || null}
            selectedOption={selectedRole}
            defaultSelect={"Select Role"}
            onSelectOption={(value) => setSelectedRole(value)}
          />
        </div>
      </div>

      {selectedRole && (
        <>
          <PermiTable data={permissions} columns={getPermissionColumns()} />
          <div className="text-end mt- mb-4 customButton">
            <button className="btn btn-primary" onClick={savePermissions}>
              Save Permissions
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PermissionTable;
